import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { Link } from '@app/core';
import { SharableLink } from '@app/form';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  containerInfo: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
    background: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    paddingBottom: 0,
    borderRadius: theme.borderRadius.std,
  },
  text: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-wrap',
  },
  link: {
    width: '100%',
  },
  linkText: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  settingFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  advancedConfig: {
    transition: 'max-height .3s',
    maxHeight: 0,
    overflow: 'hidden',

    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  advancedConfigOpened: {
    maxHeight: 800,
  },
}));

interface Props {
  text: ReactNode;
  link?: string;
  helpUrl?: string;
  children?: React.ReactNode;
  advancedUpsellModal?: () => void;
}

export const LinkConfigurationMainBlock = ({
  text,
  link,
  helpUrl,
  children,
  advancedUpsellModal,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [advancedLinkConfigOpened, setAdvancedLinkConfigOpened] =
    useState(false);
  const toggleAdvancedLinkConfig = useCallback(() => {
    if (advancedUpsellModal) {
      advancedUpsellModal();
      return;
    }
    setAdvancedLinkConfigOpened((val) => !val);
  }, [setAdvancedLinkConfigOpened, advancedUpsellModal]);

  const hideIcon = !link?.startsWith('http');

  return (
    <div className={classes.containerInfo}>
      <p className={classes.text}>{text}</p>
      {!!link && (
        <SharableLink
          hideIcon={hideIcon}
          className={classes.link}
          link={link}
        />
      )}

      {(!!helpUrl || !!children) && (
        <div className={classes.settingFooter}>
          {helpUrl ? (
            <Link
              className={classes.linkText}
              rel="noopener noreferrer"
              target="_blank"
              href={helpUrl}
            >
              {t(
                'editSession.linkConfiguration.studyLinkConfiguration.linkHelpText',
              )}
            </Link>
          ) : (
            <div />
          )}

          {!!children && (
            <Link
              className={classes.linkText}
              type="button"
              onClick={toggleAdvancedLinkConfig}
            >
              {t(
                'editSession.linkConfiguration.studyLinkConfiguration.advanced',
              )}
            </Link>
          )}
        </div>
      )}

      <div
        className={classNames(classes.advancedConfig, {
          [classes.advancedConfigOpened]: !!advancedLinkConfigOpened,
        })}
      >
        {children}
        <div />
      </div>
    </div>
  );
};
